import { findMarkerElement, ITkMarkerTarget } from './findMarkerElement';
import { getNoteContainers } from './noteContainers';

/** Сколько держится подсветка найденной метки. Совпадает с длительностью анимации. */
const FLASH_MS = 1200;

const FLASH_ATTR = 'data-tk-flash';

/** Таймеры снятия подсветки по элементам: повторный клик не должен гасить её раньше срока. */
const flashTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

/**
 * Видимость контейнера.
 *
 * `isConnected` недостаточно: заметка бывает на экране дважды, и первая копия
 * может быть скрыта стилями — скролл и вспышка ушли бы в невидимый узел.
 * `checkVisibility` есть в браузерах, но не в jsdom, поэтому там всё считается
 * видимым, как и было.
 */
function isElementVisible(element: HTMLElement): boolean {
  return typeof element.checkVisibility === 'function' ? element.checkVisibility() : true;
}

function flash(element: HTMLElement): void {
  clearTimeout(flashTimers.get(element));

  // Снять и вернуть атрибут — иначе повторный клик по той же метке не
  // перезапустит анимацию: для браузера значение атрибута не изменилось.
  element.removeAttribute(FLASH_ATTR);
  void element.offsetWidth;
  element.setAttribute(FLASH_ATTR, '');

  flashTimers.set(
    element,
    setTimeout(() => {
      element.removeAttribute(FLASH_ATTR);
      flashTimers.delete(element);
    }, FLASH_MS),
  );
}

/**
 * Скроллит к метке и коротко подсвечивает её.
 *
 * Перебираем ВИДИМЫЕ контейнеры заметки: она бывает на экране дважды
 * (разделённая доска, плавающие окна), в первом попавшемся метки может не
 * оказаться, а скрытая копия увела бы скролл в никуда. Если видимых нет,
 * отвечаем «не нашёл» — ожидание продолжится и поймает момент, когда заметка
 * появится на экране.
 */
export function revealMarker(
  noteId: string,
  target: ITkMarkerTarget,
  isVisible: (element: HTMLElement) => boolean = isElementVisible,
): boolean {
  const element = getNoteContainers(noteId)
    .filter(isVisible)
    .map((container) => findMarkerElement(container, target))
    .find((found): found is HTMLElement => found !== null);

  if (!element) {
    return false;
  }

  element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  flash(element);

  return true;
}

/**
 * Ждёт появления МЕТКИ — не контейнера.
 *
 * Контейнер монтируется раньше, чем TipTap отрисует содержимое, поэтому
 * остановка на контейнере дала бы гонку: мы бы искали метку в ещё пустом узле.
 *
 * Подписка стоит перед первой проверкой просто для наглядности. Внешнее ревью
 * утверждало, что иначе теряется мутация, случившаяся между проверкой и
 * `observe()`, — но такого окна нет: JS однопоточный, и между двумя
 * синхронными операторами чужой код DOM не меняет. Проверено перестановкой:
 * поведение и тесты одинаковы в обе стороны.
 *
 * Возвращает отмену. Звать её обязательно — по новому клику, по размонтированию
 * и после успеха, — иначе запоздавший наблюдатель уведёт экран уже после того,
 * как автор выбрал другую метку.
 */
export function waitForMarker(
  noteId: string,
  target: ITkMarkerTarget,
  timeoutMs: number,
  onFound?: () => void,
): () => void {
  let finished = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const observer = new MutationObserver(() => tryReveal());

  function stop() {
    finished = true;
    observer.disconnect();
    clearTimeout(timer);
  }

  function tryReveal() {
    if (finished || !revealMarker(noteId, target)) {
      return;
    }

    stop();
    onFound?.();
  }

  observer.observe(document.body, { childList: true, subtree: true });

  tryReveal();

  if (!finished) {
    timer = setTimeout(stop, timeoutMs);
  }

  return stop;
}
